// Shadow Onboarding Modal — 3-step express wizard that boosts profile_complete rate.
// Steps: (1) avatar upload, (2) @username + display name, (3) trading style + specialty.
// Auto-opens once per session if profile_complete=false. Dismissable.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  Loader2,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  Sparkles,
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { tShadow } from "../../i18n/shadow-social";
import { UserAvatar } from "./UserAvatar";
// Runtime-first API base: in production (*.rebelbtc.com) this resolves to
// window.location.origin so the avatar upload stays same-origin (Vercel
// rewrites /api/* to the Emergent backend). In preview it falls back to
// REACT_APP_BACKEND_URL. See /lib/api.js for the full rationale.
import { API_BASE as API } from "../../lib/api";
const TRADING_STYLES = ["scalper", "swing", "position", "investor"];
const SPECIALTIES = ["BTC", "ETH", "Altcoins", "Multi"];

const I18N = {
  title: { es: "Completa tu perfil de operador", en: "Complete your operator profile", sk: "Dokonči svoj profil", de: "Vervollständige dein Trader-Profil", "zh-CN": "完善您的交易者资料" },
  subtitle: {
    es: "3 pasos rápidos para desbloquear tu perfil público y badges automáticos.",
    en: "3 quick steps to unlock your public profile and automatic badges.",
    sk: "3 rýchle kroky na odomknutie verejného profilu a automatických odznakov.", de: "3 schnelle Schritte, um dein öffentliches Profil und automatische Badges freizuschalten.", "zh-CN": "3 个快速步骤解锁您的公开个人资料和自动徽章。",
  },
  skip: { es: "Saltar por ahora", en: "Skip for now", sk: "Preskočiť", de: "Jetzt überspringen", "zh-CN": "暂时跳过" },
  step: { es: "Paso", en: "Step", sk: "Krok", de: "Schritt", "zh-CN": "步骤" },
  back: { es: "Atrás", en: "Back", sk: "Späť", de: "Zurück", "zh-CN": "返回" },
  next: { es: "Siguiente", en: "Next", sk: "Ďalej", de: "Weiter", "zh-CN": "下一个" },
  finish: { es: "Finalizar", en: "Finish", sk: "Dokončiť", de: "Beenden", "zh-CN": "完成" },

  // Step 1
  s1Title: { es: "Sube tu foto de perfil", en: "Upload your profile photo", sk: "Nahraj profilovú fotku", de: "Lade dein Profilbild hoch", "zh-CN": "上传您的头像" },
  s1Help: { es: "JPG / PNG / WebP · max 5 MB", en: "JPG / PNG / WebP · max 5 MB", sk: "JPG / PNG / WebP · max 5 MB", de: "JPG / PNG / WebP · max. 5 MB", "zh-CN": "JPG / PNG / WebP · 最大 5 MB" },
  s1Btn: { es: "Elegir imagen", en: "Choose image", sk: "Vybrať obrázok", de: "Bild auswählen", "zh-CN": "选择图片" },
  s1Skip: {
    es: "Puedes saltar este paso y usar las iniciales por ahora.",
    en: "You can skip this step and use initials for now.",
    sk: "Tento krok môžeš preskočiť a použiť iniciály.", de: "Du kannst diesen Schritt überspringen und vorerst die Initialen verwenden.", "zh-CN": "您可以暂时跳过此步骤，使用首字母缩写。",
  },

  // Step 2
  s2Title: { es: "Elige tu @username", en: "Choose your @username", sk: "Vyber si @username", de: "Wähle deinen @Benutzernamen", "zh-CN": "选择你的 @username" },
  s2NameLabel: { es: "Nombre para mostrar", en: "Display name", sk: "Zobrazované meno", de: "Anzeigename", "zh-CN": "显示名称" },
  s2UserLabel: { es: "@username (3-20 chars · a-z 0-9 _)", en: "@username (3-20 chars · a-z 0-9 _)", sk: "@username (3-20 znakov)", de: "@username (3-20 Zeichen · a-z 0-9 _)", "zh-CN": "@username（3-20 字符 · a-z 0-9 _）" },
  s2BioLabel: { es: "Bio corta (160 caracteres)", en: "Short bio (160 chars)", sk: "Krátke bio (160 znakov)", de: "Kurze Bio (160 Zeichen)", "zh-CN": "简短简介（160 字符）" },

  // Step 3
  s3Title: { es: "Tu estilo de trading", en: "Your trading style", sk: "Tvoj štýl obchodovania", de: "Dein Trading-Stil", "zh-CN": "你的交易风格" },
  s3StyleLabel: { es: "Estilo principal", en: "Main style", sk: "Hlavný štýl", de: "Hauptstil", "zh-CN": "主要风格" },
  s3SpecLabel: { es: "Especialidad", en: "Specialty", sk: "Špecializácia", de: "Spezialität", "zh-CN": "特色菜" },

  errUsername: {
    es: "Username inválido o ya en uso.",
    en: "Invalid or taken username.",
    sk: "Neplatný alebo obsadený username.", de: "Ungültiger Benutzername oder bereits vergeben.", "zh-CN": "用户名无效或已被使用。",
  },
  errGeneric: {
    es: "Algo falló. Inténtalo de nuevo.",
    en: "Something failed. Try again.",
    sk: "Niečo zlyhalo. Skús znova.", de: "Etwas ist schiefgelaufen. Versuche es erneut.", "zh-CN": "出错了。请重试。",
  },
  done: { es: "¡Perfil completado! 🎉", en: "Profile complete! 🎉", sk: "Profil dokončený! 🎉", de: "Profil vervollständigt! 🎉", "zh-CN": "个人资料已完成！🎉" },
};

const t = (k, lang) => (I18N[k] && (I18N[k][lang] || I18N[k].es)) || k;

const ShadowOnboardingModal = ({ open, onClose, initialProfile }) => {
  const { lang } = useLanguage();
  const [step, setStep] = useState(1);
  const [avatarUrl, setAvatarUrl] = useState(initialProfile?.user?.avatar_url || null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [form, setForm] = useState({
    display_name: initialProfile?.user?.name || "",
    username: initialProfile?.user?.username || "",
    bio: initialProfile?.user?.bio || "",
    trading_style: initialProfile?.user?.trading_style || null,
    specialty: initialProfile?.user?.specialty || null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  // Reset state when re-opened
  useEffect(() => {
    if (open) {
      setStep(1);
      setError(null);
      setDone(false);
    }
  }, [open]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.("dismissed");
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/api/shadow/social/profile/avatar`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatarUrl(data.avatar_url);
    } catch {
      setError(t("errGeneric", lang));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const goNext = () => {
    setError(null);
    setStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(1, s - 1));
  };

  const submitAll = async () => {
    setSaving(true);
    setError(null);
    try {
      // Username validation client-side
      const u = (form.username || "").trim().toLowerCase();
      if (u && !/^[a-z0-9_]{3,20}$/.test(u)) {
        setError(t("errUsername", lang));
        setSaving(false);
        return;
      }
      await axios.patch(
        `${API}/api/shadow/social/profile/me`,
        {
          display_name: form.display_name,
          username: u || null,
          bio: form.bio,
          trading_style: form.trading_style,
          specialty: form.specialty,
        },
        { withCredentials: true }
      );
      setDone(true);
      setTimeout(() => onClose?.("completed"), 1100);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 || /username/i.test(detail || "")) {
        setError(t("errUsername", lang));
      } else {
        setError(t("errGeneric", lang));
      }
    } finally {
      setSaving(false);
    }
  };

  const progressPct = (step / 3) * 100;
  const previewUser = {
    name: form.display_name || initialProfile?.user?.name || "Operator",
    username: form.username,
    avatar_url: avatarUrl,
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="onb-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={() => onClose?.("dismissed")}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 90,
          background: "rgba(2,3,8,0.78)",
          backdropFilter: "blur(8px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
        data-testid="onboarding-backdrop"
      >
        <motion.div
          key="onb-modal"
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          data-testid="onboarding-modal"
          style={{
            width: "100%",
            maxWidth: 480,
            background: "#0a0b16",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 20,
            boxShadow: "0 16px 60px rgba(0,0,0,0.7)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{ padding: "20px 24px 0", position: "relative" }}>
            <button
              type="button"
              onClick={() => onClose?.("dismissed")}
              aria-label="close"
              data-testid="onboarding-close"
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.6)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <X size={16} />
            </button>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/[0.08] px-3 py-1 mb-2">
              <Sparkles className="h-3 w-3 text-amber-300" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-200">
                {t("step", lang)} {step}/3
              </span>
            </div>
            <h2
              style={{ fontSize: 20, fontWeight: 900, color: "#fff", lineHeight: 1.25 }}
              data-testid="onboarding-title"
            >
              {t("title", lang)}
            </h2>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
              {t("subtitle", lang)}
            </p>
            {/* Progress bar */}
            <div
              style={{
                marginTop: 16,
                height: 3,
                background: "rgba(255,255,255,0.08)",
                borderRadius: 99,
                overflow: "hidden",
              }}
            >
              <motion.div
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.4 }}
                style={{
                  height: "100%",
                  background: "linear-gradient(90deg,#f5a623,#ff5577)",
                }}
              />
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: "20px 24px" }} data-testid={`onboarding-step-${step}`}>
            {step === 1 && (
              <div className="flex flex-col items-center gap-3 py-3">
                <h3 className="text-sm font-bold text-white text-center">{t("s1Title", lang)}</h3>
                <UserAvatar user={previewUser} size="xl" />
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => uploadAvatar(e.target.files?.[0])}
                  data-testid="onboarding-avatar-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingAvatar}
                  data-testid="onboarding-avatar-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-amber-200 hover:bg-amber-400/15 disabled:opacity-50"
                >
                  {uploadingAvatar ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5" />
                  )}
                  {t("s1Btn", lang)}
                </button>
                <p className="text-[10px] text-white/35 text-center">{t("s1Help", lang)}</p>
                <p className="text-[11px] text-white/45 text-center mt-1">{t("s1Skip", lang)}</p>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3 py-1">
                <h3 className="text-sm font-bold text-white mb-1">{t("s2Title", lang)}</h3>
                <label className="block">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 mb-1">
                    {t("s2NameLabel", lang)}
                  </span>
                  <input
                    type="text"
                    value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    maxLength={50}
                    data-testid="onboarding-display-name"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-white focus:border-amber-400/40 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 mb-1">
                    {t("s2UserLabel", lang)}
                  </span>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-white/40">
                      @
                    </span>
                    <input
                      type="text"
                      value={form.username}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          username: e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase(),
                        })
                      }
                      maxLength={20}
                      placeholder="username"
                      data-testid="onboarding-username"
                      className="w-full rounded-xl border border-white/10 bg-black/30 pl-7 pr-4 py-2.5 text-sm text-white focus:border-amber-400/40 focus:outline-none"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 mb-1">
                    {t("s2BioLabel", lang)} · {(form.bio || "").length}/160
                  </span>
                  <textarea
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 160) })}
                    rows={2}
                    data-testid="onboarding-bio"
                    className="w-full resize-none rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-white focus:border-amber-400/40 focus:outline-none"
                  />
                </label>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4 py-1">
                <h3 className="text-sm font-bold text-white">{t("s3Title", lang)}</h3>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 mb-2">
                    {t("s3StyleLabel", lang)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {TRADING_STYLES.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() =>
                          setForm({ ...form, trading_style: form.trading_style === opt ? null : opt })
                        }
                        data-testid={`onboarding-style-${opt}`}
                        className={`rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] transition-all ${
                          form.trading_style === opt
                            ? "border-amber-400/60 bg-amber-400/[0.15] text-amber-200"
                            : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 mb-2">
                    {t("s3SpecLabel", lang)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SPECIALTIES.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() =>
                          setForm({ ...form, specialty: form.specialty === opt ? null : opt })
                        }
                        data-testid={`onboarding-spec-${opt}`}
                        className={`rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] transition-all ${
                          form.specialty === opt
                            ? "border-cyan-400/60 bg-cyan-400/[0.15] text-cyan-200"
                            : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <p
                data-testid="onboarding-error"
                className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/[0.08] px-3 py-2 text-xs text-rose-200"
              >
                {error}
              </p>
            )}
            {done && (
              <p
                data-testid="onboarding-done"
                className="mt-3 rounded-lg border border-emerald-400/30 bg-emerald-400/[0.08] px-3 py-2 text-sm font-bold text-emerald-200 inline-flex items-center gap-2"
              >
                <Check className="h-4 w-4" /> {t("done", lang)}
              </p>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "14px 24px 20px",
              borderTop: "1px solid rgba(255,255,255,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={() => onClose?.("dismissed")}
              data-testid="onboarding-skip"
              className="text-[11px] text-white/45 hover:text-white/75 transition-colors"
            >
              {t("skip", lang)}
            </button>
            <div className="flex items-center gap-2">
              {step > 1 && (
                <button
                  type="button"
                  onClick={goBack}
                  data-testid="onboarding-back"
                  className="inline-flex items-center gap-1 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white/70 hover:text-white"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  {t("back", lang)}
                </button>
              )}
              {step < 3 ? (
                <button
                  type="button"
                  onClick={goNext}
                  data-testid="onboarding-next"
                  className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-2 text-xs font-black uppercase tracking-[0.15em] text-black hover:from-amber-300 hover:to-amber-400"
                >
                  {t("next", lang)}
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submitAll}
                  disabled={saving || done}
                  data-testid="onboarding-finish"
                  className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2 text-xs font-black uppercase tracking-[0.15em] text-black hover:from-amber-300 hover:to-amber-400 disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {t("finish", lang)}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ShadowOnboardingModal;
